import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { NODE_COMPONENT } from './data';
import { Checkbox } from 'ncoded-component-library';
import utils, { getDataRoomNodeType } from 'utils';
import { DataRoomSelectableNode } from './types';
import {
  findNode,
  getIsNodeChecked,
  getIsNodeMixed,
  prepareFolderNodesForFileSelector,
  toggleAllChildren,
  toggleNode,
  updateFolderChildrenById,
} from './utils';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import useNodes from 'hooks/useNodes';
import { AxiosResponse } from 'axios';

import './FileCheckSelector.styles.scss';

type FileCheckSelectorProps = {
  className?: string;
  dataRoomNodes: DataRoomSelectableNode[];
  initiallyCheckedNodes?: string[];
  disabledLabel?: string;
  buyerLabel?: string;
  canShareBuyerFiles?: boolean;
  readOnly?: boolean;
  checkIsNodeDisabled?: (node: DataRoomNode) => boolean;
  setDataRoomNodes: React.Dispatch<
    React.SetStateAction<DataRoomSelectableNode[]>
  >;
  getChildren: (parentId?: string) => Promise<AxiosResponse<DataRoomNode[]>>;
};

const FileCheckSelector: React.FC<FileCheckSelectorProps> = (props) => {
  const {
    className,
    disabledLabel,
    buyerLabel,
    canShareBuyerFiles,
    dataRoomNodes = [],
    initiallyCheckedNodes = [],
    readOnly = false,
    checkIsNodeDisabled,
    setDataRoomNodes,
    getChildren,
  } = props;

  const classes = classNames(
    'file-check-selector',
    { 'file-check-selector--read-only': readOnly },
    className,
  );

  const { t } = useTranslation();

  const { hasFiles, hasFolders } = useNodes(dataRoomNodes);

  const selectableNodes = useMemo(
    () => dataRoomNodes.filter(({ disabled }) => !disabled),
    [dataRoomNodes],
  );

  const isNotAllChecked = useMemo(
    () => selectableNodes.every(({ checked }) => !checked),
    [selectableNodes],
  );

  const isAllMixed = useMemo(
    () =>
      !isNotAllChecked &&
      !selectableNodes.every(({ checked, isMixed }) => checked && !isMixed),
    [isNotAllChecked, selectableNodes],
  );

  const onChange = useCallback(
    (nodeId: string, checked: boolean) =>
      setDataRoomNodes((old) => toggleNode(old, nodeId, checked)),
    [setDataRoomNodes],
  );

  const onChangeAll = useCallback(
    () =>
      setDataRoomNodes((old) =>
        toggleAllChildren(old, isNotAllChecked || isAllMixed),
      ),
    [isNotAllChecked, isAllMixed, setDataRoomNodes],
  );

  const checkIsNodeMixed = useCallback(
    (node: DataRoomNode, parentChecked: boolean, parentMixed: boolean) =>
      getIsNodeMixed(
        node,
        initiallyCheckedNodes,
        parentChecked,
        parentMixed,
        canShareBuyerFiles,
      ),
    [initiallyCheckedNodes, canShareBuyerFiles],
  );

  const checkIsNodeChecked = useCallback(
    (node: DataRoomNode, parentChecked: boolean, parentMixed: boolean) =>
      getIsNodeChecked(
        node,
        initiallyCheckedNodes,
        parentChecked,
        parentMixed,
        canShareBuyerFiles,
      ),
    [initiallyCheckedNodes, canShareBuyerFiles],
  );

  const updateFolder = useCallback(
    (nodeId: string, children: DataRoomNode[]) => {
      setDataRoomNodes((old) => {
        const parentNode = findNode(old, nodeId);

        const preparedChildren = prepareFolderNodesForFileSelector(
          children,
          parentNode,
          checkIsNodeChecked,
          checkIsNodeMixed,
          checkIsNodeDisabled,
        );

        return updateFolderChildrenById(old, nodeId, preparedChildren);
      });
    },
    [
      setDataRoomNodes,
      checkIsNodeChecked,
      checkIsNodeDisabled,
      checkIsNodeMixed,
    ],
  );

  return (
    <div className={classes}>
      <div className="file-check-selector__header">
        <p>
          {hasFiles && t('files')}
          {hasFiles && hasFolders && ' & '}
          {hasFolders && t('folders')}
        </p>

        <p>{t('size')}</p>

        {/* A handler is passed even when it does nothing: the checkbox treats a
        missing `onChange` as "uncontrolled" and starts driving its own state, so
        dropping it would make this one togglable rather than inert. */}
        <Checkbox
          checked={!isNotAllChecked}
          disabled={readOnly}
          className={classNames({ mixed: isAllMixed })}
          onChange={readOnly ? utils.noop : onChangeAll}
        />
      </div>

      <div className="scroll-container">
        <ul className="file-check-selector__nodes">
          {dataRoomNodes.map((node) => {
            const nodeType = getDataRoomNodeType(node);

            return (
              <li key={node._id}>
                {NODE_COMPONENT[nodeType]({
                  node,
                  disabledLabel,
                  buyerLabel,
                  readOnly,
                  onChange,
                  updateFolder,
                  getChildren,
                })}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default FileCheckSelector;
